import { Button, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { checkDatabaseVersion, DatabaseVersion, updateDatabase } from "../../Api/System";
import PageTitle from "../atoms/PageTitle";

const DatabaseConfigPage: React.FC = () => {
    const [loading, setLoading] = React.useState(true)
    const [databaseVersion, setDatabaseVersion] = React.useState<DatabaseVersion | undefined>()
    const [requireUpdate, setRequireUpdate] = React.useState(false)
    const [updating, setUpdating] = React.useState(false)

    const { enqueueSnackbar } = useSnackbar()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const dbCheck = React.useCallback(() => {
        checkDatabaseVersion({signal: abortController.signal}).then(res => {
            setDatabaseVersion(res)
            if (res.current_version < res.latest_version) {
                setRequireUpdate(true)
            } else {
                setRequireUpdate(false)
            }
            setLoading(false)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, enqueueSnackbar])

    const doUpdate = React.useCallback(() => {
        if (databaseVersion) {
            setUpdating(true)
            updateDatabase({
                current_version: databaseVersion.current_version,
                update_version: databaseVersion.latest_version
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("データベースをアップデートしました", { variant: "success" })
                dbCheck()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            }).finally(() => {
                setUpdating(false)
            })
        }
    }, [abortController, databaseVersion, dbCheck, enqueueSnackbar])

    React.useEffect(() => {
        dbCheck()
    }, [dbCheck])

    if (loading) {
        return <Typography>Loading...</Typography>
    }
    return (
        <>
            <PageTitle text="データベース管理" />
            {requireUpdate ? (
                <>
                    <Typography>データベースのバージョンが古いため、アップデートが必要です。</Typography>
                    <Button
                        disabled={updating}
                        onClick={e => {
                            doUpdate()
                        }}
                    >データベースをアップデートする</Button>
                </>
            ) : (
                <Typography>データベースのバージョンは最新です。</Typography>
            )}
        </>
    )
}

export default DatabaseConfigPage